import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { useUserSubscription } from '../../hooks/useSubscription';
import styles from './SubscriptionExpiry.module.css';

const SubscriptionExpiry = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { subscriptionData, isLoading } = useUserSubscription();
  const [isOpen, setIsOpen] = useState(false);

  const daysRemaining = useMemo(() => {
    const sub = subscriptionData?.subscription;
    if (!sub) return null;
    if (typeof sub.daysRemaining === 'number') return sub.daysRemaining;
    if (sub.end_date) {
      const now = new Date();
      const end = new Date(sub.end_date);
      const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return Math.max(diffDays, 0);
    }
    return null;
  }, [subscriptionData]);

  useEffect(() => {
    if (!isAuthenticated || !user || isLoading) return;

    const sub = subscriptionData?.subscription;
    const isActive = sub?.isActive ?? false;
    const remaining = typeof daysRemaining === 'number' ? daysRemaining : null;

    // Show once per session after login per user
    const storageKey = user ? `subscription_expiry_shown_${user.id}` : null;
    const alreadyShown = storageKey ? sessionStorage.getItem(storageKey) === '1' : true;

    if (isActive && remaining !== null && remaining <= 7 && remaining >= 0 && !alreadyShown) {
      setIsOpen(true);
      if (storageKey) sessionStorage.setItem(storageKey, '1');
    }
  }, [isAuthenticated, user, isLoading, subscriptionData, daysRemaining]);

  if (!isOpen) return null;

  const onRenew = () => {
    setIsOpen(false);
    navigate('/subscription-plans');
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.popup} role="dialog" aria-modal="true" aria-label="Subscription Expiry Notice">
        <button className={styles.close} onClick={() => setIsOpen(false)} aria-label="Close">
          <X size={20} />
        </button>
        <div className={styles.content}>
          <h3 className={styles.title}>Your subscription is ending soon</h3>
          <p className={styles.subtitle}>
            {typeof daysRemaining === 'number' && daysRemaining >= 0
              ? `Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining.`
              : 'Please renew to continue uninterrupted access.'}
          </p>
          <div className={styles.actions}>
            <button className={styles.primary} onClick={onRenew}>Renew now</button>
            <button className={styles.secondary} onClick={() => setIsOpen(false)}>Maybe later</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionExpiry;
